import { useEffect, useState } from "react";

import { Grid, Stack, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CloseOutlined, CreateOutlined, ContentCopyOutlined } from "@mui/icons-material";

import { useConfirm } from "material-ui-confirm";
import nameof from "ts-nameof.macro";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";

import { Application, ApplicationClipboard } from "./VehicleContract";
import { PublicRoutesConfig } from "../../RoutesConfig";
import LinearStepper from "../../../common/components/LinearStepper";
import Success from "../../shared/SuccessModal";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import DropzoneExtended from "../../../common/components/mui-extended/DropzoneExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import useUserContext from "../../../hooks/useUserContext";
import { Step, useInputForm } from "../../../common/hooks/useInputForm";
import fileService from "../../../common/services/fileService";
import vehicleContractService from "../../../services/vehicleContractService";
import { VehicleContractInputDto } from "../../../interfaces/VehicleContract";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { useParams } from "react-router";

export default function Input() {
  const { id: paramsId } = useParams();
  const vehicleContractId: number = parseInt(paramsId as string);
  const [isRejected, setIsRejected] = useState(false);

  const loading = useAxiosLoader(false);
  const confirm = useConfirm();

  const { userId } = useUserContext();

  const defaultModel: VehicleContractInputDto = { userId };

  const form = useInputForm<VehicleContractInputDto>({
    defaultModel: defaultModel,
    addAsync: vehicleContractService.updateVehicleContractAsync,
    updateAsync: vehicleContractService.updateVehicleContractAsync,
  });

  useEffect(() => {
    (async () => {
      await vehicleContractService.getVehicleContractAsync(form.id).then(({ data: vehicleContract }) => {
        const model: VehicleContractInputDto = {
          ...vehicleContract,
          userId: userId,
          vehicleContractId: form.id,
          fileId: vehicleContract.file?.fileId,
        };

        form.recreateForm(model);
      });
    })();
  }, []);

  const reject = async () => {
    await vehicleContractService.rejectVehicleContractAsync(vehicleContractId).then(() => {
      setIsRejected(true);
      form.setStep(Step.SUCCESS);
    });
  };

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={PublicRoutesConfig.Vehicle}>
      {`на договор за прехвърляне на МПС ${form.getValues(
        nameof(defaultModel.registrationNumber)
      )} / ${dateToStringShort(form.getValues(nameof(defaultModel.issueDate)))}`}
    </Success>
  );

  const renderRejectedSuccess = () => (
    <Success title="Успешно прекратяване" routePath={PublicRoutesConfig.Vehicle}>
      {`на договор за прехвърляне на МПС.`}
    </Success>
  );

  const canReject = form.watch(nameof(defaultModel.registrationNumber)) === null;
  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack direction="row-reverse" spacing={1} sx={{ pb: 3 }}>
        {canReject && (
          <Button
            startIcon={<CloseOutlined />}
            onClick={() =>
              confirm({ title: "Действието ще прекрати изпълнението на услугата в СППТЕАУ. Сигурни ли сте?" })
                .then(async () => await reject())
                .catch(() => {})
            }
          >
            Прекрати
          </Button>
        )}
        <CopyToClipboard
          text={ApplicationClipboard.getText(form.watch(nameof(defaultModel.application)))}
          onCopy={() => {
            toast.success("Данните се копираха успешно!");
          }}
        >
          <Button startIcon={<ContentCopyOutlined />}>Копирай</Button>
        </CopyToClipboard>
      </Stack>

      <Stack spacing={3}>
        <SectionExtended title="Данни за договор">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Регистрационен номер"
                name={nameof(defaultModel.registrationNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{ ...Rules.Required, ...Rules.Number }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата"
                name={nameof(defaultModel.issueDate)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{ ...Rules.Required, ...Rules.Date }}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Сканирано копие">
          <DropzoneExtended
            name={nameof(defaultModel.fileId)}
            form={form}
            disabled={form.step === Step.PREVIEW}
            initRequest={fileService.getFileMetadataById}
            uploadRequest={fileService.upload}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Данни за МПС">
          {Application.renderVehicleData(form.watch(nameof(defaultModel.application)))}
        </SectionExtended>
        <SectionExtended title="Данни за продавача">
          {Application.renderOwnersData(form.watch(`${nameof(defaultModel.application)}.oldOwners`))}
        </SectionExtended>
        <SectionExtended title="Данни за купувача">
          {Application.renderOwnersData(form.watch(`${nameof(defaultModel.application)}.newOwners`))}
        </SectionExtended>
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3} minHeight={550}>
      <Typography variant="h5">Договор за прехвърляне на МПС</Typography>
      {renderStepper()}
      {isRejected ? renderRejectedSuccess() : form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
